import type { DeviceHydraItem, RegisterDevice } from "@verbleif/lib/src/composables/Api/Type/Device/device.interface";
import type { AxiosRequestConfig } from "axios";
import type { SkipTokenRefreshConfig } from "./Api/Plugin/RefreshToken/RefreshTokenResponseInterceptor";
import * as Sentry from "@sentry/vue";
import { DevicePushTokenTypeEnum, DeviceTypeEnum } from "@verbleif/lib/src/composables/Api/Type/Device/device.interface";
import { createGlobalState, StorageSerializers, useStorage } from "@vueuse/core";
import { isAxiosError } from "axios";
import { version } from "../../package.json";
import {
  computeWebDeviceSnapshot,
  DEVICE_IDENTIFIER_STORAGE_KEY,
  parseBrowserInfo,
  parseOperatingSystem,
} from "./deviceSnapshot";
import { useDeleteMutation, usePostMutation } from "./Mutation/useHydraMutations";

// Best effort during logout: a hanging request must not keep the user on the logout screen.
const UNREGISTER_DEVICE_CONFIG = {
  timeout: 5000,
  skipTokenRefresh: true,
} satisfies AxiosRequestConfig & SkipTokenRefreshConfig;

export const useDeviceState = createGlobalState(() => {
  // Persistent storage using VueUse hooks
  const deviceId = useStorage<string>(DEVICE_IDENTIFIER_STORAGE_KEY, () => crypto.randomUUID?.() || Math.random().toString(36).substring(2), localStorage);
  const browserVersion = useStorage<string>("browser_version", "", localStorage);
  const lastUpdateAt = useStorage<string>("browser_last_update", "", localStorage);
  // Server-side id of the device registered for this browser; used to delete it again on logout.
  const registeredDeviceId = useStorage<number | null>("device_registered_id", null, localStorage, { serializer: StorageSerializers.number });
  const registerMutation = usePostMutation<DeviceHydraItem, RegisterDevice>({ gcTime: 0 });
  const unregisterMutation = useDeleteMutation();

  async function updateDevice(token?: string) {
    const browserInfo = parseBrowserInfo(navigator.userAgent);
    const currentVersion = browserInfo.version;

    // First check if we have a version stored
    if (!browserVersion.value) {
      // First time running, set current date as update date
      lastUpdateAt.value = new Date().toISOString();
      browserVersion.value = currentVersion;
    } else if (browserVersion.value !== currentVersion) {
      lastUpdateAt.value = new Date().toISOString();
      browserVersion.value = currentVersion;
    } else if (!lastUpdateAt.value) {
      lastUpdateAt.value = new Date().toISOString();
    }

    const snapshot = computeWebDeviceSnapshot({
      identifier: deviceId.value,
      userAgent: navigator.userAgent,
    });
    const os = parseOperatingSystem(navigator.userAgent);

    // Ensure all fields respect max length constraints
    const name = snapshot.name.substring(0, 255);
    const brand = snapshot.brand.substring(0, 32);
    const userAgent = navigator.userAgent.substring(0, 1024);
    const model = snapshot.model.substring(0, 128);
    const operatingSystemName = os.name.substring(0, 32);
    const operatingSystemVersion = os.version.substring(0, 32);
    const fontScale = window.devicePixelRatio.toString().substring(0, 8);
    const runtimeVersion = version.substring(0, 64);
    const bundleVersion = browserInfo.version.substring(0, 64);

    // Determine channel based on hostname
    let channel: "local" | "testing" | "staging" | "production" = "production";
    if (window.location.hostname.includes("localhost")) {
      channel = "local";
    } else if (window.location.hostname.includes("dev")) {
      channel = "testing";
    } else if (window.location.hostname.includes("staging")) {
      channel = "staging";
    }

    const data: RegisterDevice = {
      identifier: deviceId.value,
      type: DeviceTypeEnum.BROWSER,
      pushTokens: token
        ? [{
            type: DevicePushTokenTypeEnum.FCM_WEB,
            token,
          }]
        : undefined,
      meta: {
        name,
        brand,
        userAgent,
        model,
        operatingSystemName,
        operatingSystemVersion,
        fontScale,
        runtimeVersion,
        buildNumber: 0,
        bundleVersion,
        lastAppUpdateAt: lastUpdateAt.value,
        emulator: false,
        channel,
      },
    };
    try {
      const response = await registerMutation.mutateAsync({ url: "/api/devices/register", body: data });
      registeredDeviceId.value = response?.id ?? null;
    } catch (error) {
      console.error("[useDeviceState] Failed to update device", error);
      Sentry.captureException(error);
    }
  }

  async function unregisterDevice() {
    const id = registeredDeviceId.value;
    if (id === null) {
      return;
    }

    try {
      await unregisterMutation.mutateAsync({ url: `/api/devices/${id}`, config: UNREGISTER_DEVICE_CONFIG });
    } catch (error) {
      // 404: the device is already gone. 401: the session is already dead, so nothing can be cleaned up with it.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404 || status === 401) {
        return;
      }
      console.error("[useDeviceState] Failed to unregister device", error);
      Sentry.captureException(error);
    } finally {
      registeredDeviceId.value = null;
    }
  }

  return {
    updateDevice,
    unregisterDevice,
    deviceId,
    registeredDeviceId,
    browserVersion,
    lastUpdateAt,
  };
});
